import React, { createContext, useState, useEffect } from 'react';
import { refreshToken, getUserProfile } from '../services/api';
import cacheInitializer from '../services/cacheInitializer';
import { useHistory } from 'react-router-dom';
import tokenManager from '../services/tokenManager';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  const handleRoleBasedRedirect = (role, isInitialLogin = false) => {
    const currentPath = window.location.pathname;
    
    if (role === 'operator') {
      // If it's initial login or we're not in any operator routes, redirect to dashboard
      if (isInitialLogin || !currentPath.startsWith('/operator')) {
        window.location.href = '/operator/dashboard';
      }
      // Otherwise, let React Router handle internal operator navigation
    } else if (role === 'admin') {
      // If it's initial login or we're not in any admin routes, redirect to admin
      if (isInitialLogin || !currentPath.startsWith('/admin')) {
        window.location.href = '/admin';
      }
      // Otherwise, let React Router handle internal admin navigation
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        // Check if token actually needs refreshing
        if (!tokenManager.shouldRefreshToken()) {
          // Token is still valid, no need to refresh
          console.log('🔑 Token is valid, skipping refresh');
          
          // Set user from localStorage if available
          const userData = localStorage.getItem('userData');
          if (userData) {
            try {
              const user = JSON.parse(userData);
              setIsAuthenticated(true);
              setUser({
                ...user,
                subscription_status: user.subscription_status
              });
              console.log('✅ Loaded cached user data, skipping token refresh');
              setIsLoading(false);
              return;
            } catch (error) {
              console.error('Error parsing cached user data:', error);
            }
          }
        }

        try {
          console.log('🔄 Token needs refresh, calling refresh endpoint');
          const response = await refreshToken();
          if (response.token && response.user) {
            setIsAuthenticated(true);
            setUser({
              ...response.user,
              subscription_status: response.user.subscription_status
            });
            localStorage.setItem('token', response.token);
            localStorage.setItem('userRole', response.user.role);
            localStorage.setItem('userData', JSON.stringify(response.user));

            // Only redirect if we're not already on the correct page
            const currentPath = window.location.pathname;
            const userRole = response.user.role;
            if (userRole === 'operator' && !currentPath.startsWith('/operator')) {
              handleRoleBasedRedirect(userRole, true);
            } else if (userRole === 'admin' && !currentPath.startsWith('/admin')) {
              handleRoleBasedRedirect(userRole, true);
            }
          } else {
            await handleLogout();
          }
        } catch (error) {
          console.error('Error verifying token:', error);
          await handleLogout();
        }
      }
      setIsLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userRole', userData.role);
    if (userData.operatorId) {
      localStorage.setItem('operatorId', userData.operatorId);
    }
    setIsAuthenticated(true);
    setUser({
      ...userData,
      subscription_status: userData.subscription_status
    });

    // Initialize user-specific cache data after login
    cacheInitializer.initializeUserData();

    // Use window.location for a full page reload on initial login
    handleRoleBasedRedirect(userData.role, true);
  };

  const handleLogout = async () => {
    // Clear all auth-related state and storage
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    setIsAuthenticated(false);
    setUser(null);

    // Reset cache initializer state
    cacheInitializer.reset();

    // Force reload to home page
    window.location.href = '/';
  };

  const updateUserSubscription = (status) => {
    setUser(prevUser => ({
      ...prevUser,
      subscription_status: status
    }));
  };

  const refreshUser = async () => {
    try {
      const response = await getUserProfile();
      console.log('Refreshed user data:', response);
      setUser(response.user);
      return response.user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
      return null;
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout: handleLogout,
      updateUserSubscription,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
